import { lazy, Suspense, type ReactNode } from "react";
import { Route } from "react-router-dom";
import PremiumRouteGuard from "@/components/PremiumRouteGuard";

// Eagerly loaded pages
import Dashboard from "@/pages/Dashboard";
import AllTools from "@/pages/AllTools";
import ProTools from "@/pages/ProTools";
import Catalogue from "@/pages/Catalogue";
import Vault from "@/pages/Vault";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import AdminProducts from "@/pages/AdminProducts";
import AdminAnnouncements from "@/pages/AdminAnnouncements";
import AdminHostedSites from "@/pages/AdminHostedSites";
import MySites from "@/pages/MySites";
import Guide from "@/pages/Guide";

// AI Tools
import AIDetector from "@/pages/AIDetector";
import AIHumanizer from "@/pages/AIHumanizer";
import Paraphraser from "@/pages/Paraphraser";
import PlagiarismChecker from "@/pages/PlagiarismChecker";
import PromptGenerator from "@/pages/PromptGenerator";

// Developer Tools
import JsonFormatter from "@/pages/JsonFormatter";
import Base64Tool from "@/pages/Base64Tool";
import HashGenerator from "@/pages/HashGenerator";
import PasswordGenerator from "@/pages/PasswordGenerator";
import UuidGenerator from "@/pages/UuidGenerator";
import CsvToJson from "@/pages/CsvToJson";
import TimestampConverter from "@/pages/TimestampConverter";

// Email Tools
import TempMail from "@/pages/TempMail";
import TempMailHistory from "@/pages/TempMailHistory";

// Document Tools
import WordCounter from "@/pages/WordCounter";
import LoremIpsumGenerator from "@/pages/LoremIpsumGenerator";
import TextCaseConverter from "@/pages/TextCaseConverter";
import MarkdownToHtml from "@/pages/MarkdownToHtml";
import DaysCalculator from "@/pages/DaysCalculator";
import UnitConverter from "@/pages/UnitConverter";

// Social Tools
import DiscordTimestamp from "@/pages/DiscordTimestamp";
import TwitterCounter from "@/pages/TwitterCounter";
import DiscordEmbedBuilder from "@/pages/DiscordEmbedBuilder";
import WebhookSender from "@/pages/WebhookSender";

// Design Tools
import QRCodeGenerator from "@/pages/QRCodeGenerator";
import ImageCompressor from "@/pages/ImageCompressor";
import ColorPicker from "@/pages/ColorPicker";
import FaviconGenerator from "@/pages/FaviconGenerator";
import ScreenshotBeautifier from "@/pages/ScreenshotBeautifier";
import EmojiPicker from "@/pages/EmojiPicker";
import ImageResizer from "@/pages/ImageResizer";
import ImageConverter from "@/pages/ImageConverter";
import BackgroundRemover from "@/pages/BackgroundRemover";
import ImageRotateFlip from "@/pages/ImageRotateFlip";
import PlaceholderImage from "@/pages/PlaceholderImage";

// Utility Tools
import QrReader from "@/pages/QrReader";
import BarcodeGenerator from "@/pages/BarcodeGenerator";
import RandomPicker from "@/pages/RandomPicker";
import CountdownTimer from "@/pages/CountdownTimer";
import PomodoroTimer from "@/pages/PomodoroTimer";

// Lazy-load heavy pages
const CodeStudio = lazy(() => import("@/pages/CodeStudio"));
const PdfAnalyzer = lazy(() => import("@/pages/PdfAnalyzer"));
const PdfMerge = lazy(() => import("@/pages/PdfMerge"));
const PdfSplitter = lazy(() => import("@/pages/PdfSplitter"));
const PdfToImage = lazy(() => import("@/pages/PdfToImage"));
const OfficeToPdf = lazy(() => import("@/pages/OfficeToPdf"));
const ResumeBuilder = lazy(() => import("@/pages/ResumeBuilder"));
const GeoAI = lazy(() => import("@/pages/GeoAI"));
const GeoViewer = lazy(() => import("@/pages/GeoViewer"));
const PdfEditor = lazy(() => import("@/pages/PdfEditor"));
const ImageCropper = lazy(() => import("@/pages/ImageCropper"));
const PdfCompressor = lazy(() => import("@/pages/PdfCompressor"));
const PdfPassword = lazy(() => import("@/pages/PdfPassword"));
const PdfWatermark = lazy(() => import("@/pages/PdfWatermark"));
const LiveSports = lazy(() => import("@/pages/LiveSports"));
const SportsWatch = lazy(() => import("@/pages/SportsWatch"));
const TamAI = lazy(() => import("@/pages/TamAI"));

const LazyFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const L = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<LazyFallback />}>{children}</Suspense>
);

export const dashboardRoutes = (
  <>
    <Route index element={<Dashboard />} />
    <Route path="tools" element={<AllTools />} />
    <Route path="pro-tools" element={<ProTools />} />
    <Route path="catalogue" element={<Catalogue />} />
    <Route path="vault" element={<Vault />} />
    <Route path="profile" element={<Profile />} />
    <Route path="settings" element={<Settings />} />
    <Route path="guide" element={<Guide />} />
    <Route path="my-sites" element={<MySites />} />

    {/* Admin */}
    <Route path="admin" element={<Admin />} />
    <Route path="admin/products" element={<AdminProducts />} />
    <Route path="admin/announcements" element={<AdminAnnouncements />} />
    <Route path="admin/hosted-sites" element={<AdminHostedSites />} />

    {/* AI Tools */}
    <Route path="tools/ai-detector" element={<AIDetector />} />
    <Route path="tools/ai-humanizer" element={<AIHumanizer />} />
    <Route path="tools/paraphraser" element={<Paraphraser />} />
    <Route path="tools/plagiarism-checker" element={<PlagiarismChecker />} />
    <Route path="tools/prompt-generator" element={<PromptGenerator />} />
    <Route path="tools/tam-ai" element={<L><TamAI /></L>} />

    {/* Developer Tools */}
    <Route path="tools/code" element={<L><CodeStudio /></L>} />
    <Route path="tools/code-studio" element={<L><CodeStudio /></L>} />
    <Route path="tools/json-formatter" element={<JsonFormatter />} />
    <Route path="tools/base64" element={<Base64Tool />} />
    <Route path="tools/hash-generator" element={<HashGenerator />} />
    <Route path="tools/password-generator" element={<PasswordGenerator />} />
    <Route path="tools/uuid-generator" element={<UuidGenerator />} />
    <Route path="tools/csv-json" element={<CsvToJson />} />
    <Route path="tools/timestamp-converter" element={<TimestampConverter />} />

    {/* Email Tools */}
    <Route path="tools/temp-mail" element={<TempMail />} />
    <Route path="tools/temp-mail/history" element={<TempMailHistory />} />

    {/* Document Tools */}
    <Route path="tools/word-counter" element={<WordCounter />} />
    <Route path="tools/lorem-ipsum" element={<LoremIpsumGenerator />} />
    <Route path="tools/text-case" element={<TextCaseConverter />} />
    <Route path="tools/markdown-html" element={<MarkdownToHtml />} />
    <Route path="tools/days-calculator" element={<DaysCalculator />} />
    <Route path="tools/unit-converter" element={<UnitConverter />} />

    {/* PDF Tools */}
    <Route path="tools/pdf-analyzer" element={<L><PdfAnalyzer /></L>} />
    <Route path="tools/pdf-merge" element={<L><PdfMerge /></L>} />
    <Route path="tools/pdf-splitter" element={<L><PdfSplitter /></L>} />
    <Route path="tools/pdf-to-image" element={<L><PdfToImage /></L>} />
    <Route path="tools/office-to-pdf" element={<L><OfficeToPdf /></L>} />
    <Route path="tools/resume-builder" element={<L><ResumeBuilder /></L>} />
    <Route path="tools/pdf-editor" element={<L><PdfEditor /></L>} />
    <Route path="tools/pdf-compressor" element={<L><PdfCompressor /></L>} />
    <Route path="tools/pdf-password" element={<L><PdfPassword /></L>} />
    <Route path="tools/pdf-watermark" element={<L><PdfWatermark /></L>} />

    {/* Social Tools */}
    <Route path="tools/discord-timestamp" element={<DiscordTimestamp />} />
    <Route path="tools/twitter-counter" element={<TwitterCounter />} />
    <Route path="tools/discord-embed" element={<DiscordEmbedBuilder />} />
    <Route path="tools/webhook-sender" element={<WebhookSender />} />

    {/* Design Tools */}
    <Route path="tools/qr-code" element={<QRCodeGenerator />} />
    <Route path="tools/image-compressor" element={<ImageCompressor />} />
    <Route path="tools/color-picker" element={<ColorPicker />} />
    <Route path="tools/favicon-generator" element={<FaviconGenerator />} />
    <Route path="tools/screenshot-beautifier" element={<ScreenshotBeautifier />} />
    <Route path="tools/emoji-picker" element={<EmojiPicker />} />
    <Route path="tools/image-resizer" element={<ImageResizer />} />
    <Route path="tools/image-cropper" element={<L><ImageCropper /></L>} />
    <Route path="tools/image-converter" element={<ImageConverter />} />
    <Route path="tools/background-remover" element={<BackgroundRemover />} />
    <Route path="tools/image-rotate-flip" element={<ImageRotateFlip />} />
    <Route path="tools/placeholder-image" element={<PlaceholderImage />} />

    {/* Geo Tools */}
    <Route path="tools/geo-ai" element={<L><GeoAI /></L>} />
    <Route path="tools/geo-viewer" element={<L><GeoViewer /></L>} />

    {/* Utility Tools */}
    <Route path="tools/qr-reader" element={<QrReader />} />
    <Route path="tools/barcode-generator" element={<BarcodeGenerator />} />
    <Route path="tools/random-picker" element={<RandomPicker />} />
    <Route path="tools/countdown-timer" element={<CountdownTimer />} />
    <Route path="tools/pomodoro-timer" element={<PomodoroTimer />} />

    {/* Pro/Premium Tools */}
    <Route path="tools/live-sports" element={<PremiumRouteGuard><L><LiveSports /></L></PremiumRouteGuard>} />
    <Route path="tools/live-sports/watch/:id" element={<PremiumRouteGuard><L><SportsWatch key="channel" /></L></PremiumRouteGuard>} />
    <Route path="tools/live-sports/cricket/:id" element={<PremiumRouteGuard><L><SportsWatch key="cricket" /></L></PremiumRouteGuard>} />
    <Route path="tools/live-sports/match/:id" element={<PremiumRouteGuard><L><SportsWatch key="match" /></L></PremiumRouteGuard>} />
  </>
);
